import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaSyncAlt,
  FaUserCheck,
  FaTimesCircle,
  FaBan,
  FaUndo,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";
import api from "../../libs/axios";
import { useAuth } from "../../provider/auth.provider";
import { useConfirm } from "../../provider/confirm.provider";
import { Pagination, PaginationType } from "../../utils/pagination.utils";
import toast from "react-hot-toast";
import { dateRangeError, dateRangeInputBounds } from "../../utils/date-range.utils";

// Operations > Site Survey > Requested Sites.
// Field engineers ask, from the mobile app, to work a survey site that is still
// at Site Created or Engineer Assigned. This is where those requests are turned
// into an assignment or given a status.
const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "assigned", label: "Assigned" },
  { key: "rejected", label: "Rejected" },
  { key: "closed", label: "Closed" },
  { key: "withdrawn", label: "Withdrawn" },
  { key: "", label: "All" },
];

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  assigned: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  closed: "bg-gray-200 text-gray-700",
  withdrawn: "bg-slate-100 text-slate-600",
};

const STAGE_LABEL: Record<string, string> = {
  site_created: "Site Created",
  engineer_assigned: "Engineer Assigned",
};

const stageText = (v?: string | null) => (v ? STAGE_LABEL[v] || v.replace(/_/g, " ") : "-");
const fullName = (u: any) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || "-" : "-");
const dateTimeText = (v?: string | null) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const RequestedSitesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { confirm, prompt } = useConfirm();
  const canManage = hasPermission("Site", "site_requests_manage");

  const [rows, setRows] = useState<any[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<PaginationType | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState("pending");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const load = useCallback(async () => {
    // The list refetches on every filter change, so an over-long range is
    // stopped here; the table keeps its last rows until the range is fixed.
    const dateError = dateRangeError(dateFrom, dateTo, "Requested date");
    if (dateError) {
      toast.error(dateError, { id: "requested-sites-date-range" });
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/survey/site-requests", {
        params: {
          page,
          limit,
          status: status || undefined,
          search: search || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      const body = res.data?.data ?? {};
      setRows(body.requests || []);
      setCounts(body.counts || {});
      setPagination(body.pagination || null);
    } catch {
      setRows([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  // The axios interceptor already toasts the server's success / error message
  // for every POST and PATCH, so these handlers only refresh.
  const run = async (id: number, action: () => Promise<unknown>) => {
    setBusyId(id);
    try {
      await action();
      await load();
    } catch {
      /* surfaced by the interceptor */
    } finally {
      setBusyId(null);
    }
  };

  const assign = async (r: any) => {
    const current = r.site?.site_engineer;
    const takingOver = current && Number(current.id) !== Number(r.requested_by);
    const ok = await confirm({
      title: `Assign ${fullName(r.requester)} to ${r.site?.site_id || "this site"}`,
      message: takingOver
        ? `${fullName(r.requester)} becomes this site's Site Engineer, replacing ${fullName(current)}.`
        : `${fullName(r.requester)} becomes this site's Site Engineer.`,
      warning: "Any other pending requests for this site are closed, and every engineer involved is notified.",
      confirmText: "Assign engineer",
      cancelText: "Not now",
      variant: takingOver ? "warning" : "info",
    });
    if (!ok) return;
    const remarks = await prompt({
      title: "Assignment remarks",
      message: "Optional — recorded on the request and on the site's workflow history.",
      label: "Remarks",
      placeholder: "Add a note (optional)",
      multiline: true,
      maxLength: 2000,
      required: false,
      confirmText: "Assign",
      cancelText: "Cancel",
      variant: "info",
    });
    if (remarks === null) return;
    await run(r.id, () => api.post(`/survey/site-requests/${r.id}/assign`, { remarks: remarks.trim() || undefined }));
  };

  const setRequestStatus = async (r: any, next: "rejected" | "closed" | "pending") => {
    if (next === "pending") {
      const ok = await confirm({
        title: "Reopen request",
        message: `Move ${fullName(r.requester)}'s request for ${r.site?.site_id || "this site"} back to pending?`,
        confirmText: "Reopen",
        cancelText: "Cancel",
        variant: "info",
      });
      if (!ok) return;
      await run(r.id, () => api.patch(`/survey/site-requests/${r.id}/status`, { status: "pending" }));
      return;
    }
    const remarks = await prompt({
      title: next === "rejected" ? "Reject request" : "Close request",
      message:
        next === "rejected"
          ? `${fullName(r.requester)} is notified that their request was rejected.`
          : `Close the request without assigning. ${fullName(r.requester)} is notified.`,
      label: "Remarks",
      placeholder: next === "rejected" ? "Why is this request being rejected?" : "Why is this request being closed?",
      required: true,
      multiline: true,
      maxLength: 2000,
      confirmText: next === "rejected" ? "Reject" : "Close request",
      cancelText: "Cancel",
      variant: "danger",
    });
    if (remarks === null || !remarks.trim()) return;
    await run(r.id, () => api.patch(`/survey/site-requests/${r.id}/status`, { status: next, remarks: remarks.trim() }));
  };

  const columnCount = canManage ? 12 : 11;
  const dateBounds = dateRangeInputBounds(dateFrom, dateTo);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Assign Request</h1>
          <p className="text-xs text-gray-600 mt-1">
            Survey sites field engineers have asked to work. A site can be requested only while it is at Site Created or
            Engineer Assigned. Assigning makes the engineer the site's Site Engineer.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-3 mb-4 space-y-3">
          <div className="flex flex-wrap gap-1">
            {STATUS_TABS.map((t) => {
              const active = status === t.key;
              const count = t.key ? counts[t.key] : counts.total;
              return (
                <button
                  key={t.key || "all"}
                  onClick={() => {
                    setStatus(t.key);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {t.label}
                  {typeof count === "number" && (
                    <span className={`ml-1.5 text-xs ${active ? "text-blue-100" : "text-gray-500"}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search site ID, site name, engineer or employee code"
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Requested from</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                {...dateBounds.from}
                className="px-2 py-2 text-sm border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Requested to</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                {...dateBounds.to}
                className="px-2 py-2 text-sm border border-gray-300 rounded"
              />
            </div>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              <FaSyncAlt className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Requested On</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Site ID</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Site Name</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Distance Away</th>
                  <th className="px-3 py-2 text-left">Project</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Stage</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Current Site Engineer</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Requested By</th>
                  <th className="px-3 py-2 text-left">Engineer's Remarks</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Actioned</th>
                  {canManage && <th className="px-3 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={columnCount} className="px-3 py-10 text-center text-gray-500">
                      Loading requests...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="px-3 py-10 text-center text-gray-500">
                      No site requests found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const site = r.site;
                    const busy = busyId === r.id;
                    const isPending = r.status === "pending";
                    const canReopen = r.status === "rejected" || r.status === "closed";
                    return (
                      <tr key={r.id} className="align-top hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{dateTimeText(r.created_at)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {site ? (
                            <Link to={`/sites/${site.id}`} className="inline-flex items-center gap-1 font-medium text-blue-800 hover:underline">
                              {site.site_id} <FaExternalLinkAlt className="w-2.5 h-2.5" />
                            </Link>
                          ) : (
                            <span className="text-gray-400">#{r.site_id}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{site?.site_name || "-"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700" title="Distance from the engineer when they requested the site">
                          {r.distance_km != null ? (
                            <span className="inline-flex items-center gap-1">
                              <FaMapMarkerAlt className="w-2.5 h-2.5 text-gray-400" /> {Number(r.distance_km)} km
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{r.project?.project_name || site?.project?.project_name || "-"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-gray-800">{stageText(site?.survey_workflow_status)}</div>
                          {site?.status === "cancelled" || site?.status === "hold" ? (
                            <div className="text-xs text-red-600">Site {site.status}</div>
                          ) : null}
                          {r.site_workflow_status_at_request && r.site_workflow_status_at_request !== site?.survey_workflow_status && (
                            <div className="text-xs text-gray-400">At request: {stageText(r.site_workflow_status_at_request)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                          {site?.site_engineer ? fullName(site.site_engineer) : <span className="text-gray-400">Unassigned</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-gray-800">{fullName(r.requester)}</div>
                          <div className="text-xs text-gray-500">
                            {[r.requester?.employee_code, r.requester?.phone_number].filter(Boolean).join(" · ") || "-"}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-700 max-w-xs break-words">{r.remarks || "-"}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${STATUS_BADGE[r.status] || "bg-gray-100 text-gray-700"}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 min-w-[160px]">
                          {r.actioned_at ? (
                            <>
                              <div>{dateTimeText(r.actioned_at)}</div>
                              <div className="text-gray-500">{r.actioner ? fullName(r.actioner) : "System"}</div>
                              {r.action_remarks && <div className="text-gray-500 break-words">{r.action_remarks}</div>}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        {canManage && (
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {isPending && (
                                <>
                                  <button
                                    onClick={() => assign(r)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-white bg-green-700 rounded hover:bg-green-800 disabled:opacity-60"
                                  >
                                    <FaUserCheck className="w-3 h-3" /> Assign
                                  </button>
                                  <button
                                    onClick={() => setRequestStatus(r, "rejected")}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-red-700 border border-red-200 bg-red-50 rounded hover:bg-red-100 disabled:opacity-60"
                                  >
                                    <FaTimesCircle className="w-3 h-3" /> Reject
                                  </button>
                                  <button
                                    onClick={() => setRequestStatus(r, "closed")}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
                                  >
                                    <FaBan className="w-3 h-3" /> Close
                                  </button>
                                </>
                              )}
                              {canReopen && (
                                <button
                                  onClick={() => setRequestStatus(r, "pending")}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-blue-800 border border-blue-200 bg-blue-50 rounded hover:bg-blue-100 disabled:opacity-60"
                                >
                                  <FaUndo className="w-3 h-3" /> Reopen
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
              limitOptions={[10, 25, 50, 100]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestedSitesPage;
